import React, {
	useEffect,
	useState,
	useCallback,
} from 'react';
import ListingConfig from './ListingConfig.js';

import style from './AccountSettings.module.css';
import CustomIcon from '../Common/CustomIcon/CustomIcon';
import TextSmall from 'Vulture/Field/TextSmall.js';
import {
	getFDValue,
} from 'Vulture/Helpers.js';
import {
	buildTestData,
} from 'Vulture/TestData.js';
import ArrayOptions from './ArrayOptions.js';
import MessagingOptions from './MessagingOptions.js';
import {
	deleteAccount, fetchDataSets,
} from 'Vulture/Account.js';
import Select from 'react-select';

import {
	flushAll as flushFormInputs,
} from 'Vulture/lib/FormFlush.js';

import {
	advertisingFeeds,
	applyFeedToggle,
	defaultVCLProvidersJSON,
	infotainmentFeeds,
	resolutionOptions,
} from './VCLSettings.js';
import DurationPicker from './DurationPicker.js';
import {
	broncoURL,
} from 'Vulture/ENV.js';
import {
	getSession,
} from 'Vulture/Session';

const DeleteInputField = (props) => {
	const {
		setConfirmModalContent,
	} = props;

	const [ deleteField, setDeleteField, ] = useState('');
	const [ deleteConfirmed, setDeleteConfirmed, ] = useState(false);

	return (
		<React.Fragment>
			<input
				value={deleteField}
				onChange={(e) => {
					if (e.target.value === 'DELETE') {
						setDeleteConfirmed(true);
					} else {
						setDeleteConfirmed(false);
					}
					setDeleteField(e.target.value);
				}}

			/>
			<button
				className='button'
				onClick={(e) => {
					e.preventDefault();
					setConfirmModalContent('');
				}}
			>
				Cancel
			</button>
			<button
				type='submit'
				className={`button ${deleteConfirmed ? '' : 'button-disabled no-click'}`}
			>
				DELETE
			</button>
		</React.Fragment>
	);
};

const LISTINGS = 'listings';
const BASIC = 'basic';
const SECURITY = 'security';
const ARRAYS = 'arrays';
const MESSAGING = 'messaging';

const AccountSettingsView = (props) => {
	const {
		app,
		accountUUID,
		fd,
		pendingChanges,
		settingMap,
	} = props;

	// Parsed account.array_options. Keys are the dataNames available as
	// listing_column display_name options. Passed down to ListingColumnConfig
	// so it doesn't need to dip into fd for this.
	const arrayOptionsRaw = fd[`account.${accountUUID}`]?.array_options;
	let arrayOptions = {};
	if (arrayOptionsRaw) {
		try {
			arrayOptions = JSON.parse(arrayOptionsRaw);
		} catch (err) {
			console.error('Failed to parse account.array_options:', err);
		}
	}

	const [ confirmModalContent, setConfirmModalContent, ] = useState('');
	const [ dataSets, setDataSets, ] = useState({});
	const [ messageLabels, setMessageLabels, ] = useState([]);
	const [ isLoading, setIsLoading, ] = useState(false);
	const [ activeTab, setActiveTab, ] = useState(LISTINGS);

	// Get initial tab from URL hash or default to LISTINGS
	const getInitialTab = () => {
		const hash = window.location.hash.substring(1); // Remove the # character
		const validTabs = [ LISTINGS, BASIC, SECURITY, ARRAYS, MESSAGING, ];
		return validTabs.includes(hash) ? hash : LISTINGS;
	};

	// Initialize active tab from URL
	useEffect(() => {
		setActiveTab(getInitialTab());
	}, []);

	// Handle tab changes and update URL
	const handleTabChange = (tabId) => {
		setActiveTab(tabId);
		// Update URL hash without triggering a page reload
		window.history.replaceState(null, null, `#${tabId}`);
	};

	// Listen for hash changes (back/forward navigation)
	useEffect(() => {
		const handleHashChange = () => {
			setActiveTab(getInitialTab());
		};

		window.addEventListener('hashchange', handleHashChange);
		return () => {
			return window.removeEventListener('hashchange', handleHashChange);
		};
	}, []);

	// Tab configuration
	const tabs = [
		{
			id:         LISTINGS,
			label:      'Listing Config',
			hasChanges: () => {
				return hasListingConfigChanges();
			},
		},
		{
			id:         BASIC,
			label:      'Basic Information',
			hasChanges: () => {
				return hasBasicInfoChanges();
			},
		},
		{
			id:         SECURITY,
			label:      'Access & Features',
			hasChanges: () => {
				return hasSecurityChanges();
			},
		},
		{
			id:         ARRAYS,
			label:      'Array Options',
			hasChanges: () => {
				return hasArrayOptionsChanges();
			},
		},
		{
			id:         MESSAGING,
			label:      'Messaging',
			hasChanges: () => {
				return hasMessagingChanges();
			},
		},
	];

	// Helper function to check if a section has pending changes
	const hasSectionChanges = (sectionKeys) => {
		if (!pendingChanges) return false;
		return sectionKeys.some((key) => {
			return Object.keys(pendingChanges).some((pendingKey) => {
				return pendingKey.includes(key);
			});
		}
		);
	};

	// Section-specific change detection
	const hasBasicInfoChanges = () => {
		return hasSectionChanges([
			'display_name', 'salesforce_id_val',
		]);
	};

	const hasSecurityChanges = () => {
		return hasSectionChanges([
			'require_2fa', 'allow_slideshow_url', 'show_billing', 'animation_phase_toggle', 'animation_phase_options', 'allow_feed_publishing',
		]);
	};

	const hasArrayOptionsChanges = () => {
		return hasSectionChanges([
			'array_options',
		]);
	};

	const hasMessagingChanges = () => {
		return hasSectionChanges([
			'message_labels', 'notification_settings',
		]);
	};

	const hasListingConfigChanges = () => {
		return hasSectionChanges([
			'listing_config', 'column_metadata',
		]);
	};


	// Fetch messaging labels on mount
	const fetchLabels = useCallback(async () => {
		const url = `${broncoURL}/messaging/labels/${accountUUID}`;
		let sessionKey = getSession().session;

		try {
			const response = await fetch(url, {
				method:  'GET',
				headers: {
					'Content-Type':   'application/json',
					'X-Auth-Session': sessionKey,
				},
			});

			const labels = await response.json();
			const formattedLabels = labels.map((label) => {
				return {
					id:    label.ID,
					value: label.UUID,
					label: label.Label,
				};
			});

			setMessageLabels(formattedLabels);
		} catch (err) {
			console.error('Error fetching labels:', err);
		}
	}, [ accountUUID, ]);

	useEffect(() => {
		const loadData = async () => {
			setIsLoading(true);
			try {
				await app.fetchAccount(accountUUID);
				// Fetch labels immediately when component mounts
				await fetchLabels();
			} catch (error) {
				console.error('Error loading account data:', error);
			} finally {
				setIsLoading(false);
			}
		};

		app.load(loadData);
		fetchDataSets(setDataSets);
	}, [ accountUUID, app, fetchLabels, ]);

	// Enable natural page scrolling when component mounts
	useEffect(() => {
		// Store original overflow value
		const originalHtmlOverflow = document.documentElement.style.overflow;
		const originalBodyOverflow = document.body.style.overflow;

		// Enable scrolling
		document.documentElement.style.overflow = 'auto';
		document.body.style.overflow = 'auto';

		// Cleanup: restore original values when component unmounts
		return () => {
			document.documentElement.style.overflow = originalHtmlOverflow;
			document.body.style.overflow = originalBodyOverflow;
		};
	}, []);

	const hasSaveBlocks = useCallback(() => {
		const saveBlocks = [];

		const pendingArrayOptionsChanges = pendingChanges?.[`account.${accountUUID}.array_options`];

		if (pendingArrayOptionsChanges) {
			const parsedJson = JSON.parse(pendingArrayOptionsChanges);

			Object.entries(parsedJson).forEach(([ dataName, arrayOptionsSet, ]) => {
				if (!Array.isArray(arrayOptionsSet?.options)) {
					saveBlocks.push(`- ${dataName}'s options must be an array`);
					return;
				}

				arrayOptionsSet.options.forEach((option) => {
					if (!(typeof option === 'object' && !Array.isArray(option) && option !== null)) {
						saveBlocks.push(`- ${dataName}'s options must be objects`);
						return;
					}

					if (typeof option.label !== 'string') {
						saveBlocks.push(`- ${dataName}'s labels must be strings`);
						return;
					}

					switch (arrayOptionsSet.type) {
					case 'string':
					case 'text':
						if (!(typeof option.value === 'string')) {
							saveBlocks.push(`- ${dataName}'s values must be strings`);
							return;
						}
						break;
					case 'float':
						if (!(typeof option.value === 'number')) {
							saveBlocks.push(`- ${dataName}'s values must be floats`);
							return;
						}
						break;
					case 'int':
						if (!(typeof option.value === 'number') || option.value !== Math.floor(option.value)) {
							saveBlocks.push(`- ${dataName}'s values must be integers`);
							return;
						}
						break;
					case 'bool':
						if (!(typeof option.value === 'boolean')) {
							saveBlocks.push(`- ${dataName}'s values must be booleans`);
							return;
						}
						break;

					default:
						console.log(`The type ${arrayOptionsSet.type} has no validation logic implemented.`);
					}
				});
			});
		}

		return saveBlocks;
	}, [ accountUUID, pendingChanges, ]);

	const saveFunction = useCallback(async () => {
		const saveBlocks = hasSaveBlocks();

		if (saveBlocks.length > 0) {
			const saveBlockMessage = saveBlocks.join('\n');
			alert(saveBlockMessage);
			return;
		}

		// Commit any in-focus input's typed value into context / pendingChanges
		// before reading queue state. flushFormInputs awaits one tick so React
		// commits the queued setStates from each flusher first.
		await flushFormInputs();

		setIsLoading(true);
		app.saveData('account_settings', async () => {
			app.runPendingPromises();
			// runPendingPromiseGroups drains queued listing_config thunks then
			// fires each group's onSettled — which (for listingConfig) runs
			// the deferred concatenator regens against the just-committed
			// column_metadata. Awaiting keeps the loading spinner up until
			// those regens (and the trailing refetch) actually finish.
			await app.runPendingPromiseGroups();
			setIsLoading(false);
		});
	}, [ app, hasSaveBlocks, ]);

	// Early return with loading state if no data yet
	if (Object.keys(fd).length === 0) {
		return (
			<div className={style.pageContainer}>
				<div className={style.loadingOverlay}>
					<div className={style.loadingSpinner}>
						<div className={style.spinner} />
						<p>Loading account settings...</p>
					</div>
				</div>
			</div>
		);
	}

	if (!fd[`account.${accountUUID}`]) {
		return (
			<div className={style.pageContainer}>
				<div className={style.pageContent}>
					<div className={style.pageHeader}>
						<h2>Account Settings</h2>
					</div>
					<div style={{
						textAlign: 'center',
						padding:   '2rem',
						color:     '#6c757d',
					}}>
						<p>Account not found or access denied.</p>
					</div>
				</div>
			</div>
		);
	}

	const displayName = getFDValue(fd, `account.${accountUUID}.display_name`);
	const salesforceIDVal = getFDValue(fd, `account.${accountUUID}.salesforce_id_val`);

	const handleDeleteAccount = async () => {
		const data = await deleteAccount(fd, accountUUID);

		if (data?.err) {
			alert('Please make sure the accountUUID is not associated to any other users.');
		} else if (data) {
			app.redirect({
				to:     '/accounts',
				reload: true,
			});
		}
	};

	const handleMessageLabelsUpdate = (labels) => {
		setMessageLabels(labels);
	};

	const deleteAccountPreflightCheck = () => {
		const children = [];
		const childrenTexts = [];

		for (const [ m1Key, m1Data, ] of Object.entries(fd)) {
			if (m1Key === `account.${accountUUID}`) {
				for (const m2Key of Object.keys(m1Data)) {
					if (m2Key.split('.').length === 2) {
						children.push(m2Key);
					}
				}
			}
		}

		for (const [ m1Key, m1Data, ] of Object.entries(fd)) {
			if (children.includes(m1Key)) {
				for (const [ m2Key, m2Data, ] of Object.entries(m1Data)) {
					if (m2Key === 'display_name') {
						childrenTexts.push(`${m1Key}: ${m2Data}`);
					}
				}
			}
		}

		showCannotDeleteModal(childrenTexts);
	};

	const showCannotDeleteModal = (children) => {
		setConfirmModalContent(
			<div className="confirm-modal-container">
				<div className="confirm-modal-content">
					<div>
						<p>By hitting DELETE you will be deleting all of the following child components.  These cannot be recovered.  Enter the word “DELETE” below to continue or Cancel to exit.</p>
						{children.map((child, i) => {
							return <p key={i}>{child.split(': ')[1]}</p>;
						})}
						<form onSubmit={(e) => {
							e.preventDefault();
							handleDeleteAccount();
						}}>
							<div className="confirm-modal-buttons">
								<DeleteInputField setConfirmModalContent={setConfirmModalContent}/>
							</div>
						</form>
					</div>
				</div>
			</div>
		);
	};

	let saveButtonClass = 'button button-disabled button-save-lg no-click';
	if (app.hasPendingChanges() || app.hasPendingPromises()) {
		saveButtonClass = 'button button-save-lg';
	}

	if (salesforceIDVal === '' || displayName === '') {
		saveButtonClass = 'button button-disabled button-save-lg no-click';
	}

	const isVisualContentLoopEnabled = getFDValue(fd, `account.${accountUUID}.is_visual_content_loop_enabled`);

	const isAnimationPhaseEnabled = getFDValue(fd, `account.${accountUUID}.animation_phase_toggle`);
	const animationPhaseOptionsPath = `account.${accountUUID}.animation_phase_options`;
	const animationPhaseOptionsRaw = getFDValue(fd, animationPhaseOptionsPath) || '{"options":[]}';
	let animationPhaseOptionsJSON = { options: [], };
	try {
		animationPhaseOptionsJSON = JSON.parse(animationPhaseOptionsRaw);
		if (!animationPhaseOptionsJSON.options) {
			animationPhaseOptionsJSON.options = [];
		}
	} catch (error) {
		console.error('Error parsing animation_phase_options JSON:', error);
	}

	const accountVCLProvidersPath = `account.${accountUUID}.visual_content_loop_providers`;
	const visualContentLoopProviders = getFDValue(fd, accountVCLProvidersPath) || JSON.stringify(defaultVCLProvidersJSON);

	let visualContentLoopProvidersJSON = {
		...defaultVCLProvidersJSON,
	};

	try {
		visualContentLoopProvidersJSON = JSON.parse(visualContentLoopProviders);
	} catch (error) {
		console.error('Error parsing visual_content_loop_providers JSON:', error);
	}

	const vclJSONSettings = visualContentLoopProvidersJSON['settings'] || {};
	const vclJSONAdvertising = visualContentLoopProvidersJSON['advertising'] || {};
	const vclJSONInfotainment = visualContentLoopProvidersJSON['infotainment'] || {};

	const handleDurationChange = (feedName, newDuration, feedType) => {
		const updatedVCL = {
			...visualContentLoopProvidersJSON,
		};

		if (feedType === 'advertising') {
			updatedVCL.advertising[feedName].duration = newDuration;
		} else if (feedType === 'infotainment') {
			updatedVCL.infotainment[feedName].duration = newDuration;
		}

		const nextValStr = JSON.stringify(updatedVCL);
		app.setValue('account_settings', [ accountVCLProvidersPath, ], [ nextValStr, ]);
	};

	return (
		<div className={style.pageContainer}>
			{confirmModalContent}
			{isLoading && (
				<div className={style.loadingOverlay}>
					<div className={style.loadingSpinner}>
						<div className={style.spinner} />
						<p>Loading account settings...</p>
					</div>
				</div>
			)}
			<div className={style.pageContent}>
				<div className={style.pageHeader}>
					<h2>Account Settings</h2>
					<div className={style.buttons}>
						<button
							className='button button-save-lg alert-button'
							onClick={(e) => {
								deleteAccountPreflightCheck();
								// TODO: API.deleteRecord('m1', m1ID)
								// TODO: handle error(has_children)
								// TODO: handle success(no_children)

								// eslint-disable-next-line no-debugger
								debugger;
							}}
							data-testing-info={buildTestData('button--delete')}
							data-test-id='accountSettingsDeleteAccountButton'
							disabled={isLoading}
						>
							<CustomIcon icon='trash' /> Delete Account
						</button>
						<button
							onClick={() => {
								saveFunction();
							}}
							className={saveButtonClass}
							data-testing-info={buildTestData('button--save')}
							disabled={isLoading}
						>
							<CustomIcon icon='save' /> {isLoading ? 'Saving...' : 'Save Changes'}
						</button>
					</div>
				</div>

				<div className={style.accountContent}>
					{/* Tab Navigation */}
					<div className={style.tabNavigation}>
						{tabs.map((tab) => {
							return (
								<button
									key={tab.id}
									className={`${style.tabButton} ${activeTab === tab.id ? style.tabButtonActive : ''} ${tab.hasChanges() ? style.tabButtonHasChanges : ''}`}
									data-test-id="accountSettingsTabButton"
									onClick={() => {
										return handleTabChange(tab.id);
									}}
								>
									{tab.label}
									{tab.hasChanges() && <span className={style.changeIndicator}>•</span>}
								</button>
							);
						})}
					</div>

					{/* Tab Content */}
					<div className={style.tabContent}>
						{/* Basic Information Tab */}
						{activeTab === BASIC && (
							<div className={style.tabPanel}>
								<div className={style.settingsGrid}>
									<div className={style.column1}>
										<div className={style.settingsField}>
											<div
												className={style.sectionLabel}
												onClick={(e) => {
													e.preventDefault();
													let url = `https://touchsource.lightning.force.com/lightning/r/Account/${salesforceIDVal}/view`;
													window.open(url, '_blank').focus();
												}}
											>
												<CustomIcon icon='external-link-alt' />
												Salesforce Integration
											</div>
											<TextSmall
												value={salesforceIDVal}
												setValue={app.setValue.bind(app, 'account_settings', `account.${accountUUID}.salesforce_id_val`)}
												dataTestingInfo={buildTestData('input--salesforce')}
											/>
										</div>
									</div>
									<div className={style.column2}>
										<div className={style.settingsField}>
											<label className={style.label}>Display Name</label>
											<TextSmall
												value={displayName}
												setValue={app.setValue.bind(app, 'account_settings', `account.${accountUUID}.display_name`)}
												dataTestingInfo={buildTestData('input--details')}
											/>
										</div>
										<div className={style.settingsField}>
											<label className={style.label}>Account UUID</label>
											<input
												disabled={true}
												value={accountUUID}
												data-testing-info={buildTestData('input--uuid')}
											/>
										</div>
									</div>
								</div>
							</div>
						)}

						{/* Security & Access Settings Tab */}
						{activeTab === SECURITY && (
							<div className={style.tabPanel}>
								<div className={style.settingsGrid}>
									<div className={style.column2}>
										<div className={style.checkboxContainer} data-test-id="settingsCheckboxContainer">
											<div className={style.checkboxLabel}>Two Factor Authentication</div>
											<div className={style.checkbox} data-test-id="settingsCheckbox">
												<div
													className={getFDValue(fd, `account.${accountUUID}.require_2fa`) ? style.activeCheckbox : style.disabledSquare}
													onClick={(e) => {
														e.preventDefault();
														const nextVal = !getFDValue(fd, `account.${accountUUID}.require_2fa`);
														const keys = [
															`account.${accountUUID}.require_2fa`,
														];

														const vals = [
															nextVal,
														];
														app.setValue('account_settings', keys, vals);
													}}>
													<div className={style.checkboxIcon}>
														<CustomIcon icon="check"/>
													</div>
												</div>
											</div>
										</div>
									</div>
									<div className={style.column2}>
										<div className={style.checkboxContainer} data-test-id="settingsCheckboxContainer">
											<div className={style.checkboxLabel}>Allow Slideshow/Slide Deck URLs</div>
											<div className={style.checkbox} data-test-id="settingsCheckbox">
												<div
													className={getFDValue(fd, `account.${accountUUID}.allow_slideshow_url`) ? style.activeCheckbox : style.disabledSquare}
													onClick={(e) => {
														e.preventDefault();
														const nextVal = !getFDValue(fd, `account.${accountUUID}.allow_slideshow_url`);
														const keys = [
															`account.${accountUUID}.allow_slideshow_url`,
														];

														const vals = [
															nextVal,
														];
														app.setValue('account_settings', keys, vals);
													}}>
													<div className={style.checkboxIcon}>
														<CustomIcon icon="check"/>
													</div>
												</div>
											</div>
										</div>
									</div>
									<div className={style.column2}>
										<div className={style.checkboxContainer} data-test-id="settingsCheckboxContainer">
											<div className={style.checkboxLabel}>Enable Visual Content Loop +</div>
											<div className={style.checkbox} data-test-id="settingsCheckbox">
												<div
													className={getFDValue(fd, `account.${accountUUID}.is_visual_content_loop_enabled`) ? style.activeCheckbox : style.disabledSquare}
													onClick={(e) => {
														e.preventDefault();
														const nextVal = !getFDValue(fd, `account.${accountUUID}.is_visual_content_loop_enabled`);
														const keys = [
															`account.${accountUUID}.is_visual_content_loop_enabled`,
															accountVCLProvidersPath,
														];

														const nextAccountVCLProvidersPath = JSON.stringify(visualContentLoopProvidersJSON);

														const vals = [
															nextVal,
															nextAccountVCLProvidersPath,
														];

														app.setValue('account_settings', keys, vals);
													}}>
													<div className={style.checkboxIcon}>
														<CustomIcon icon="check"/>
													</div>
												</div>
											</div>
										</div>
									</div>
									<div className={style.column2}>
										<div className={style.checkboxContainer} data-test-id="settingsCheckboxContainer">
											<div className={style.checkboxLabel}>Enable In-Folder Scheduling</div>
											<div className={style.checkbox} data-test-id="settingsCheckbox">
												<div
													className={getFDValue(fd, `account.${accountUUID}.is_in_folder_scheduling_enabled`) ? style.activeCheckbox : style.disabledSquare}
													onClick={(e) => {
														e.preventDefault();
														const nextVal = !getFDValue(fd, `account.${accountUUID}.is_in_folder_scheduling_enabled`);
														app.setValue('account_settings', [
															`account.${accountUUID}.is_in_folder_scheduling_enabled`,
														], [
															nextVal,
														]);
													}}>
													<div className={style.checkboxIcon}>
														<CustomIcon icon="check"/>
													</div>
												</div>
											</div>
										</div>
									</div>
									<div className={style.column2}>
										<div className={style.checkboxContainer}>
											<div className={style.checkboxLabel}>Show Billing Information</div>
											<div className={style.checkbox}>
												<div
													className={getFDValue(fd, `account.${accountUUID}.show_billing`) ? style.activeCheckbox : style.disabledSquare}
													onClick={(e) => {
														e.preventDefault();
														const nextVal = !getFDValue(fd, `account.${accountUUID}.show_billing`);
														const keys = [
															`account.${accountUUID}.show_billing`,
														];

														const vals = [
															nextVal,
														];
														app.setValue('account_settings', keys, vals);
													}}>
													<div className={style.checkboxIcon}>
														<CustomIcon icon="check"/>
													</div>
												</div>
											</div>
										</div>
									</div>
									<div className={style.column2}>
										<div className={style.checkboxContainer}>
											<div className={style.checkboxLabel}>Enable Active Screen Dropdown</div>
											<div className={style.checkbox}>
												<div
													className={isAnimationPhaseEnabled ? style.activeCheckbox : style.disabledSquare}
													onClick={(e) => {
														e.preventDefault();
														const nextVal = !isAnimationPhaseEnabled;
														const keys = [
															`account.${accountUUID}.animation_phase_toggle`,
														];

														const vals = [
															nextVal,
														];
														app.setValue('account_settings', keys, vals);
													}}>
													<div className={style.checkboxIcon}>
														<CustomIcon icon="check"/>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>

								{/* Active Screen Dropdown Options Section */}
								{isAnimationPhaseEnabled && (
									<div className={style.visualContentSection}>
										<div className={style.visualContentHeader}>
											<h3>Active Screen Dropdown Options</h3>
										</div>
										<div className={style.categorySection}>
											<table className={style.contentTable}>
												<thead className={style.tableHeader}>
													<tr>
														<th>Label</th>
														<th>Value</th>
														<th></th>
													</tr>
												</thead>
												<tbody>
													{animationPhaseOptionsJSON.options.map((option, index) => {
														return (
															<tr key={index} className={style.tableRow}>
																<td className={`${style.tableCell} ${style.itemLabelCell}`}>
																	<TextSmall
																		value={option.label || ''}
																		setValue={(newLabel) => {
																			const updated = { ...animationPhaseOptionsJSON, };
																			updated.options = [ ...updated.options, ];
																			updated.options[index] = { ...updated.options[index], label: newLabel, };
																			app.setValue('account_settings', [ animationPhaseOptionsPath, ], [ JSON.stringify(updated), ]);
																		}}
																	/>
																</td>
																<td className={`${style.tableCell} ${style.itemLabelCell}`}>
																	<TextSmall
																		value={option.value || ''}
																		setValue={(newValue) => {
																			const updated = { ...animationPhaseOptionsJSON, };
																			updated.options = [ ...updated.options, ];
																			updated.options[index] = { ...updated.options[index], value: newValue, };
																			app.setValue('account_settings', [ animationPhaseOptionsPath, ], [ JSON.stringify(updated), ]);
																		}}
																	/>
																</td>
																<td className={style.tableCell}>
																	<div
																		style={{ cursor: 'pointer', }}
																		onClick={() => {
																			const updated = { ...animationPhaseOptionsJSON, };
																			updated.options = updated.options.filter((_, i) => {
																				return i !== index;
																			});
																			app.setValue('account_settings', [ animationPhaseOptionsPath, ], [ JSON.stringify(updated), ]);
																		}}
																	>
																		<CustomIcon icon='minus-circle' />
																	</div>
																</td>
															</tr>
														);
													})}
												</tbody>
											</table>
											<div style={{ marginTop: '10px', }}>
												<button
													className='button'
													onClick={(e) => {
														e.preventDefault();
														const updated = { ...animationPhaseOptionsJSON, };
														updated.options = [ ...updated.options, { label: '', value: '', }, ];
														app.setValue('account_settings', [ animationPhaseOptionsPath, ], [ JSON.stringify(updated), ]);
													}}
												>
													<CustomIcon icon='plus' /> Add Option
												</button>
											</div>
										</div>
									</div>
								)}

								{/* Visual Content Loop + Section */}
								{isVisualContentLoopEnabled && (
									<div className={style.visualContentSection}>
										<div className={style.visualContentHeader}>
											<h3>Third Party Content Options</h3>
										</div>

										<div className={style.contentCategories}>
											{/* General Settings Section */}
											<div className={style.categorySection}>
												<h4 className={style.categoryTitle}>General Settings</h4>
												<table className={style.contentTable}>
													<thead className={style.tableHeader}>
														<tr>
															<th>Setting</th>
															<th>Value</th>
														</tr>
													</thead>
													<tbody>
														<tr className={style.tableRow}>
															<td className={`${style.tableCell} ${style.itemLabelCell}`}>
																<span>Resolution</span>
															</td>
															<td className={`${style.tableCell} ${style.parametersCell}`}>
																<div className={style.parameterGroup}>
																	<div className={style.parameterItem}>
																		<Select
																			hideSelectedOptions={false}
																			className={style.buttonsDropdown + ' ' + style.autoWidthSelect}
																			styles={{}}
																			options={resolutionOptions}
																			onChange={(e) => {
																				const newValue = e.value;

																				vclJSONSettings['resolution'] = newValue;
																				visualContentLoopProvidersJSON['settings'] = vclJSONSettings;

																				const nextValStr = JSON.stringify(visualContentLoopProvidersJSON);

																				app.setValue('account_settings', [ accountVCLProvidersPath, ], [ nextValStr, ]);
																			}}
																			value={resolutionOptions.find((option) => {
																				return option.value === vclJSONSettings['resolution'];
																			})}
																		/>
																	</div>
																</div>
															</td>
														</tr>
														<tr className={style.tableRow}>
															<td className={`${style.tableCell} ${style.itemLabelCell}`}>
																<span>Allow Audio</span>
															</td>
															<td className={`${style.tableCell} ${style.parametersCell}`}>
																<div className={style.checkboxContainer}>
																	<div className={style.checkbox}>
																		<div
																			className={vclJSONSettings['allow_audio'] ? style.activeCheckbox : style.disabledSquare}
																			onClick={(e) => {
																				e.preventDefault();
																				const nextVal = !vclJSONSettings['allow_audio'];

																				vclJSONSettings['allow_audio'] = nextVal;
																				visualContentLoopProvidersJSON['settings'] = vclJSONSettings;

																				const nextValStr = JSON.stringify(visualContentLoopProvidersJSON);

																				app.setValue('account_settings', [ accountVCLProvidersPath, ], [ nextValStr, ]);
																			}}>
																			<div className={style.checkboxIcon}>
																				<CustomIcon icon="check"/>
																			</div>
																		</div>
																	</div>
																</div>
															</td>
														</tr>
													</tbody>
												</table>
											</div>

											{/* Advertising Section */}
											<div className={style.categorySection}>
												<h4 className={style.categoryTitle}>Advertising</h4>
												<table className={style.contentTable}>
													<thead className={style.tableHeader}>
														<tr>
															<th>Service</th>
															<th>Duration</th>
															<th>Parameters</th>
														</tr>
													</thead>
													<tbody>
														{advertisingFeeds.map(({
															name: itemName,
															options,
															autoSelectOptions,
														}, index) => {
															const feedName = `${itemName.toLowerCase().replace(/\s+/g, '_')}`;
															vclJSONAdvertising[feedName] = vclJSONAdvertising[feedName] || {};
															vclJSONAdvertising[feedName]['options'] = vclJSONAdvertising[feedName]['options'] || {};
															const feedData = vclJSONAdvertising[feedName];
															const isEnabled = feedData['enabled'] || false;

															return (
																<tr key={index} className={style.tableRow}>
																	<td className={`${style.tableCell} ${style.itemLabelCell}`}>
																		<div className={style.checkboxContainer}>
																			<div className={style.checkbox}>
																				<div
																					className={isEnabled ? style.activeCheckbox : style.disabledSquare}
																					onClick={(e) => {
																						e.preventDefault();
																						const nextVal = !isEnabled;
																						applyFeedToggle(vclJSONAdvertising[feedName], {
																							options,
																							autoSelectOptions,
																						}, nextVal);

																						visualContentLoopProvidersJSON['advertising'] = vclJSONAdvertising;

																						const nextValStr = JSON.stringify(visualContentLoopProvidersJSON);

																						app.setValue('account_settings', [ accountVCLProvidersPath, ], [ nextValStr, ]);
																					}}
																				>
																					<div className={style.checkboxIcon}>
																						<CustomIcon icon="check"/>
																					</div>
																				</div>
																			</div>
																			<span>{itemName}</span>
																		</div>
																	</td>
																	<td className={`${style.tableCell} ${style.itemLabelCell}`}>
																		<DurationPicker
																			value={15}
																			readOnly={true}
																		/>
																	</td>
																	<React.Fragment>
																		{Object.entries(options || {}).map(([ optionsLabel, selectionOptions, ]) => {
																			return (
																				<td className={`${style.tableCell} ${style.parametersCell}`} key={`${itemName}_${optionsLabel}`}>
																					{(() => {
																						const selectedOptions = feedData?.['options']?.[optionsLabel];
																						// Nova: one valid operator, stamped on enable and not user-editable.
																						const isFixedOption = (autoSelectOptions || []).includes(optionsLabel);

																						if (optionsLabel && selectionOptions) {
																							return (
																								<div className={style.parameterGroup}>
																									<div className={style.parameterItem}>
																										<label className={style.parameterLabel}>{optionsLabel}</label>

																										<Select
																											isDisabled={isFixedOption}
																											hideSelectedOptions={false}
																											className={style.buttonsDropdown + ' ' + style.autoWidthSelect}
																											styles={{}}
																											options={selectionOptions}
																											onChange={(e) => {
																												const nextVal = e.value;
																												vclJSONAdvertising[feedName]['options'][optionsLabel] = nextVal;
																												visualContentLoopProvidersJSON['advertising'] = vclJSONAdvertising;

																												const nextValStr = JSON.stringify(visualContentLoopProvidersJSON);

																												app.setValue('account_settings', [ accountVCLProvidersPath, ], [ nextValStr, ]);
																											}}
																											value={isFixedOption ? selectionOptions[0] : selectionOptions.find((option) => {
																												return option.value === selectedOptions;
																											})}
																										/>
																									</div>
																								</div>
																							);
																						}
																						return '-';
																					})()}
																				</td>
																			);
																		})}
																	</React.Fragment>
																</tr>
															);
														})}
													</tbody>
												</table>
											</div>

											{/* Published Feeds Section */}
											<div className={style.categorySection}>
												<h4 className={style.categoryTitle}>Published Feeds</h4>
												<table className={style.contentTable}>
													<thead className={style.tableHeader}>
														<tr>
															<th>Setting</th>
															<th>Value</th>
														</tr>
													</thead>
													<tbody>
														<tr className={style.tableRow}>
															<td className={`${style.tableCell} ${style.itemLabelCell}`}>
																<span>Allow Feed Publishing</span>
															</td>
															<td className={`${style.tableCell} ${style.parametersCell}`}>
																<div className={style.checkboxContainer} data-test-id="settingsCheckboxContainer">
																	<div className={style.checkbox} data-test-id="settingsCheckbox">
																		<div
																			className={getFDValue(fd, `account.${accountUUID}.allow_feed_publishing`) ? style.activeCheckbox : style.disabledSquare}
																			onClick={(e) => {
																				e.preventDefault();
																				const nextVal = !getFDValue(fd, `account.${accountUUID}.allow_feed_publishing`);
																				app.setValue('account_settings', [
																					`account.${accountUUID}.allow_feed_publishing`,
																				], [
																					nextVal,
																				]);
																			}}>
																			<div className={style.checkboxIcon}>
																				<CustomIcon icon="check"/>
																			</div>
																		</div>
																	</div>
																</div>
															</td>
														</tr>
													</tbody>
												</table>
											</div>

											{/* Infotainment Section */}
											<div className={style.categorySection}>
												<h4 className={style.categoryTitle}>Infotainment</h4>
												<table className={style.contentTable}>
													<thead className={style.tableHeader}>
														<tr>
															<th>Service</th>
															<th>Duration</th>
															<th>Parameters</th>
														</tr>
													</thead>
													<tbody>
														{infotainmentFeeds.map(({
															name: itemName,
															options,
														}, index) => {
															const feedName = `${itemName.toLowerCase().replace(/\s+/g, '_')}`;
															vclJSONInfotainment[feedName] = vclJSONInfotainment[feedName] || {};
															vclJSONInfotainment[feedName]['options'] = vclJSONInfotainment[feedName]['options'] || {};
															const feedData = vclJSONInfotainment[feedName];
															const isEnabled = feedData['enabled'] || false;

															return (
																<tr key={index} className={style.tableRow}>
																	<td className={`${style.tableCell} ${style.itemLabelCell}`}>
																		<div className={style.checkboxContainer}>
																			<div className={style.checkbox}>
																				<div
																					className={isEnabled ? style.activeCheckbox : style.disabledSquare}
																					onClick={(e) => {
																						e.preventDefault();
																						const nextVal = !isEnabled;
																						vclJSONInfotainment[feedName]['enabled'] = nextVal;
																						visualContentLoopProvidersJSON['infotainment'] = vclJSONInfotainment;

																						const nextValStr = JSON.stringify(visualContentLoopProvidersJSON);

																						app.setValue('account_settings', [ accountVCLProvidersPath, ], [ nextValStr, ]);
																					}}
																				>
																					<div className={style.checkboxIcon}>
																						<CustomIcon icon="check"/>
																					</div>
																				</div>
																			</div>
																			<span>{itemName}</span>
																		</div>
																	</td>
																	<td className={`${style.tableCell} ${style.itemLabelCell}`}>
																		<DurationPicker
																			value={feedData.duration || 7}
																			onChange={(newDuration) => {
																				handleDurationChange(feedName, newDuration, 'infotainment');
																			}}
																		/>
																	</td>
																	<React.Fragment>
																		{Object.entries(options || {}).map(([ optionsLabel, selectionOptions, ]) => {
																			return (
																				<td className={`${style.tableCell} ${style.parametersCell}`} key={`${itemName}_${optionsLabel}`}>
																					{(() => {
																						const selectedOptions = feedData?.['options']?.[optionsLabel];

																						if (optionsLabel && options) {
																							return (
																								<div className={style.parameterGroup}>
																									<div className={style.parameterItem}>
																										<label className={style.parameterLabel}>{optionsLabel}</label>

																										<Select
																											isMulti={true}
																											allowSelectAll={true}
																											closeMenuOnSelect={false}
																											hideSelectedOptions={false}
																											className={style.buttonsDropdown + ' ' + style.autoWidthSelect}
																											styles={{}}
																											options={selectionOptions}
																											onChange={(e) => {
																												const nextVal = e.map((item) => {
																													return item.value;
																												}).join(',');

																												vclJSONInfotainment[feedName]['options'][optionsLabel] = nextVal;
																												visualContentLoopProvidersJSON['infotainment'] = vclJSONInfotainment;

																												const nextValStr = JSON.stringify(visualContentLoopProvidersJSON);

																												app.setValue('account_settings', [ accountVCLProvidersPath, ], [ nextValStr, ]);
																											}}
																											value={selectionOptions.filter((option) => {
																												return (selectedOptions || '').split(',').includes(option.value);
																											})}
																										/>
																									</div>
																								</div>
																							);
																						}
																						return '-';
																					})()}
																				</td>
																			);
																		})}
																	</React.Fragment>
																</tr>
															);
														})}
												</tbody>
											</table>
										</div>

										{/* Additional Settings Section */}
										<div className={style.categorySection}>
											<h4 className={style.categoryTitle}>Additional Settings</h4>
											<table className={style.contentTable}>
												<tbody>
													<tr className={style.tableRow}>
														<td className={`${style.tableCell} ${style.itemLabelCell}`}>
															<div className={style.checkboxContainer}>
																<div className={style.checkbox}>
																	<div
																		className={vclJSONSettings['proxied'] ? style.activeCheckbox : style.disabledSquare}
																		onClick={(e) => {
																			e.preventDefault();
																			const nextVal = !vclJSONSettings['proxied'];

																			vclJSONSettings['proxied'] = nextVal;
																			visualContentLoopProvidersJSON['settings'] = vclJSONSettings;

																			const nextValStr = JSON.stringify(visualContentLoopProvidersJSON);

																			app.setValue('account_settings', [ accountVCLProvidersPath, ], [ nextValStr, ]);
																		}}>
																		<div className={style.checkboxIcon}>
																			<CustomIcon icon="check"/>
																		</div>
																	</div>
																</div>
																<span>Proxy Media via TouchSource</span>
															</div>
														</td>
													</tr>
												</tbody>
											</table>
											</div>
										</div>
									</div>
								)}
							</div>
						)}

						{/* Array Options Tab */}
						{activeTab === ARRAYS && (
							<div className={style.tabPanel}>
								<ArrayOptions
									app={app}
									accountUUID={accountUUID}
									fd={fd}
									pendingChanges={pendingChanges}
									settingMap={settingMap}
								/>
							</div>
						)}

						{/* Messaging Options Tab */}
						{activeTab === MESSAGING && (
							<div className={style.tabPanel}>
								<MessagingOptions
									app={app}
									accountUUID={accountUUID}
									fd={fd}
									pendingChanges={pendingChanges}
									settingMap={settingMap}
									onMessageLabelsUpdate={handleMessageLabelsUpdate}
								/>
							</div>
						)}

						{/* Listing Configuration Tab */}
						{activeTab === LISTINGS && (
							<div className={style.tabPanel}>
								<ListingConfig
									app={app}
									accountUUID={accountUUID}
									dataSets={dataSets}
									pendingChanges={pendingChanges}
									settingMap={settingMap}
									messageLabels={messageLabels}
									arrayOptions={arrayOptions}
								/>
							</div>
						)}
					</div>
				</div>
				<div className={style.pageFooter}>
					<div className={style.buttons}>
						<button
							onClick={() => {
								saveFunction();
							}}
							className={saveButtonClass}
							data-testing-info={buildTestData('button--save')}
							disabled={isLoading}
						>
							<CustomIcon icon='save' /> {isLoading ? 'Saving...' : 'Save Changes'}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AccountSettingsView;